import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, Activity, RefreshCw, AlertCircle, Search } from "lucide-react";
import { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/main-layout";

interface StockQuote {
  symbol: string;
  price: number;
  bidPrice: number;
  askPrice: number;
  volume: number;
  change: number;
  changePercent: number;
  timestamp: string;
}

interface MarketMovers {
  gainers: StockQuote[];
  losers: StockQuote[];
  mostActive: StockQuote[];
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(price);
}

function formatChange(change: number, percent: number): string {
  const sign = change >= 0 ? "+" : "";
  return `${sign}${change.toFixed(2)} (${sign}${percent.toFixed(2)}%)`;
}

function StockRow({ stock }: { stock: StockQuote }) {
  const isPositive = stock.changePercent >= 0;
  
  return (
    <div 
      className="flex items-center justify-between py-3 px-4 border-b last:border-b-0 hover-elevate"
      data-testid={`stock-row-${stock.symbol}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center">
          {isPositive ? (
            <TrendingUp className="h-5 w-5 text-green-500" />
          ) : (
            <TrendingDown className="h-5 w-5 text-red-500" />
          )}
        </div>
        <div>
          <div className="font-semibold" data-testid={`text-symbol-${stock.symbol}`}>
            {stock.symbol}
          </div>
          <div className="text-xs text-muted-foreground">
            Volume: {stock.volume.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono font-semibold" data-testid={`text-price-${stock.symbol}`}>
          {formatPrice(stock.price)}
        </div>
        <Badge 
          variant={isPositive ? "default" : "destructive"}
          className="text-xs"
          data-testid={`badge-change-${stock.symbol}`}
        >
          {formatChange(stock.change, stock.changePercent)}
        </Badge>
      </div>
    </div>
  );
}

interface QuoteResponse {
  quote?: {
    BidPrice: number;
    AskPrice: number;
    BidSize: number;
    AskSize: number;
  };
  trade?: {
    Price: number;
  };
}

function QuoteLookup() {
  const [symbol, setSymbol] = useState("");
  const [searchSymbol, setSearchSymbol] = useState<string | null>(null);
  
  const { data: quote, isLoading, error } = useQuery<QuoteResponse>({
    queryKey: ["/api/alpaca/quote", searchSymbol],
    enabled: !!searchSymbol,
  });
  
  const handleSearch = () => {
    if (symbol.trim()) {
      setSearchSymbol(symbol.toUpperCase().trim());
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          Quote Lookup
        </CardTitle>
        <CardDescription>Search for real-time stock quotes</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-4">
          <Input
            placeholder="Enter symbol (e.g., AAPL)"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            data-testid="input-symbol-search"
          />
          <Button 
            onClick={handleSearch}
            disabled={isLoading}
            data-testid="button-search-quote"
          >
            {isLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : "Search"}
          </Button>
        </div>
        
        {error && (
          <div className="text-sm text-destructive flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Failed to fetch quote. Make sure Alpaca is configured.
          </div>
        )}
        
        {quote && searchSymbol && (
          <div className="space-y-3 p-4 bg-muted rounded-md">
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold">{searchSymbol}</span>
              <span className="text-2xl font-mono font-bold">
                {formatPrice(quote.trade?.Price || 0)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Bid:</span>{" "}
                <span className="font-mono">{formatPrice(quote.quote?.BidPrice || 0)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Ask:</span>{" "}
                <span className="font-mono">{formatPrice(quote.quote?.AskPrice || 0)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Bid Size:</span>{" "}
                <span className="font-mono">{quote.quote?.BidSize || 0}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Ask Size:</span>{" "}
                <span className="font-mono">{quote.quote?.AskSize || 0}</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface BrokerageStatusData {
  alpaca: { configured: boolean; mode: string; features: string[] };
  tradier: { configured: boolean; mode: string; features: string[] };
  coinbase: { configured: boolean; mode: string; features: string[] };
  kraken: { configured: boolean; mode: string; features: string[] };
  interactiveBrokers: { configured: boolean; mode: string; features: string[] };
}

export default function MarketMovers() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  const { data: brokerageStatus } = useQuery<BrokerageStatusData>({
    queryKey: ["/api/brokerages/status"],
  });
  
  const { data: movers, isLoading, refetch, isRefetching } = useQuery<MarketMovers>({
    queryKey: ["/api/market-movers"],
    refetchInterval: autoRefresh ? 10000 : false,
  });
  
  useEffect(() => {
    const interval = autoRefresh ? setInterval(() => refetch(), 10000) : null;
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, refetch]);
  
  const alpacaConfigured = brokerageStatus?.alpaca?.configured;

  return (
    <MainLayout
      title="Market Movers"
      subtitle="Real-time market data powered by Alpaca"
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            data-testid="button-toggle-autorefresh"
          >
            {autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isRefetching}
            data-testid="button-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isRefetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      }
    >
      <div className="space-y-6">

      {!alpacaConfigured && (
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-medium">Alpaca API Not Configured</p>
                <p className="text-sm text-muted-foreground">
                  To see live market data, you need to configure your Alpaca API credentials in the Brokerage Settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <QuoteLookup />
        
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Brokerage Connections</CardTitle>
            <CardDescription>Status of connected trading platforms</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {brokerageStatus && Object.entries(brokerageStatus).map(([key, status]: [string, any]) => (
                <div 
                  key={key}
                  className="p-3 border rounded-md text-center"
                  data-testid={`brokerage-status-${key}`}
                >
                  <div className="font-medium capitalize text-sm">
                    {key === "interactiveBrokers" ? "IB" : key}
                  </div>
                  <Badge 
                    variant={status.configured ? "default" : "secondary"}
                    className="mt-1 text-xs"
                  >
                    {status.configured ? status.mode : "Not Connected"}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="gainers">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="gainers" data-testid="tab-gainers">
            <TrendingUp className="h-4 w-4 mr-2" />
            Top Gainers
          </TabsTrigger>
          <TabsTrigger value="losers" data-testid="tab-losers">
            <TrendingDown className="h-4 w-4 mr-2" />
            Top Losers
          </TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">
            <Activity className="h-4 w-4 mr-2" />
            Most Active
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="gainers">
          <Card>
            <CardHeader>
              <CardTitle className="text-green-500">Top Gainers</CardTitle>
              <CardDescription>Stocks with the highest price increase</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-8 text-center text-muted-foreground">
                  Loading market data...
                </div>
              ) : movers?.gainers?.length ? (
                <div data-testid="gainers-list">
                  {movers.gainers.map((stock) => (
                    <StockRow key={stock.symbol} stock={stock} />
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  {alpacaConfigured 
                    ? "No market data available. Markets may be closed." 
                    : "Configure Alpaca API to view market data."}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="losers">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-500">Top Losers</CardTitle>
              <CardDescription>Stocks with the highest price decrease</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-8 text-center text-muted-foreground">
                  Loading market data...
                </div>
              ) : movers?.losers?.length ? (
                <div data-testid="losers-list">
                  {movers.losers.map((stock) => (
                    <StockRow key={stock.symbol} stock={stock} />
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  {alpacaConfigured 
                    ? "No market data available. Markets may be closed." 
                    : "Configure Alpaca API to view market data."}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="active">
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-500">Most Active</CardTitle>
              <CardDescription>Stocks with the highest trading volume</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-8 text-center text-muted-foreground">
                  Loading market data...
                </div>
              ) : movers?.mostActive?.length ? (
                <div data-testid="active-list">
                  {movers.mostActive.map((stock) => (
                    <StockRow key={stock.symbol} stock={stock} />
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  {alpacaConfigured 
                    ? "No market data available. Markets may be closed." 
                    : "Configure Alpaca API to view market data."}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </MainLayout>
  );
}
